import React from 'react';
import { Button, InputNumber, Popover, Segmented, Select, Tooltip } from 'antd';
import { AlignCenterOutlined, AlignLeftOutlined, AlignRightOutlined, CopyOutlined,
  DeleteOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TGDComponent } from '../../../models/courses/graphicDesign';
import { FONT_PAIRS } from '../core/fonts';
import { TGDConstraints } from '../core/document';
import ColorsPanel from './panels/ColorsPanel';

interface GDContextToolbarProps {
  selected?: TGDComponent; currentColor: string; constraints: TGDConstraints;
  onUpdate: (patch: Partial<TGDComponent>) => void;
  onColor: (color: string, background: boolean) => void;
  onDuplicate: () => void; onForward: () => void; onBackward: () => void; onDelete: () => void;
}

const GDContextToolbar: React.FC<GDContextToolbarProps> = ({ selected, currentColor,
  constraints, onUpdate, onColor, onDuplicate, onForward, onBackward, onDelete }) => {
  const { t } = useTranslation();
  if (!selected) return <div className="gd-context gd-context-empty" />;
  const action = (key: string, icon: React.ReactNode, handler: () => void) =>
    <Tooltip title={t(`graphicDesignLesson.editor.${key}`)}><Button icon={icon}
      aria-label={t(`graphicDesignLesson.editor.${key}`)} onClick={handler} /></Tooltip>;
  return <div className="gd-context">
    <Popover trigger="click" content={<ColorsPanel currentColor={currentColor}
      constraints={constraints} onColor={onColor} />}>
      <Button aria-label={t('graphicDesignLesson.editor.colors')}>
        <span className="gd-color-dot" style={{ background: selected.fill }} />
      </Button>
    </Popover>
    {selected.type === 'text' && <>
      <Select aria-label={t('graphicDesignLesson.editor.fonts')} className="gd-font-select"
        value={selected.fontPair ?? FONT_PAIRS[0].id}
        options={FONT_PAIRS.map(pair => ({ value: pair.id, label: pair.label }))}
        onChange={fontPair => onUpdate({ fontPair })} />
      <Button aria-label={t('graphicDesignLesson.editor.decreaseSize')}
        onClick={() => onUpdate({ fontSize: (selected.fontSize ?? 16) - 1 })}>−</Button>
      <InputNumber min={8} max={400} value={selected.fontSize}
        aria-label={t('graphicDesignLesson.editor.fontSize')}
        onChange={value => { if (value !== null) onUpdate({ fontSize: value }); }} />
      <Button aria-label={t('graphicDesignLesson.editor.increaseSize')}
        onClick={() => onUpdate({ fontSize: (selected.fontSize ?? 16) + 1 })}>+</Button>
      <Segmented value={selected.fontWeight ?? 400} options={[400, 600, 800]}
        onChange={value => onUpdate({ fontWeight: value as 400 | 600 | 800 })} />
      {action('alignLeft', <AlignLeftOutlined />, () => onUpdate({ align: 'left' }))}
      {action('alignCenter', <AlignCenterOutlined />, () => onUpdate({ align: 'center' }))}
      {action('alignRight', <AlignRightOutlined />, () => onUpdate({ align: 'right' }))}
      <Segmented value={selected.direction ?? 'ltr'} options={['ltr', 'rtl']}
        onChange={value => onUpdate({ direction: value as 'ltr' | 'rtl' })} />
    </>}
    {action('duplicate', <CopyOutlined />, onDuplicate)}
    {action('forward', <UpOutlined />, onForward)}
    {action('backward', <DownOutlined />, onBackward)}
    {action('delete', <DeleteOutlined />, onDelete)}
    <span className="gd-rotation">{Math.round(selected.rotation)}°</span>
  </div>;
};

export default GDContextToolbar;
